import {Button} from '@material-ui/core';
import {FieldInputProps, Form, Formik} from 'formik';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory, useLocation, useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {projectFetch} from '../../store/project';
import {InputType, SinglePageApplication} from '../../types/project/Application';
import {Project} from '../../types/project/Project';
import {RootState, useAppDispatch} from '../../store';
import cn from 'classnames';
import {LoadingSkeleton} from './LoadingSkeleton';
import {ReturnToPreviousPageButton} from '../../base-components/StudioButton/ReturnToPreviousPageButton';
import {usePostProcessorList} from '../../api/postprocessor/GetPostProcessorList';
import {setONNXTask, SPA_VIDEO_SOURCES, TASK_STATUS} from '../TestDeployView/useDeploy';
import {OpenVXRuntimeContainer} from './OpenVXRuntimeContainer';
import {
  FetchAppPostProcessor,
  FetchModelPostProcessor,
  FetchPreProcessor,
  FetchSPAPostProcessor,
  ONNXRuntimeContainer,
  supportsImages,
  supportsVideo,
} from '../ONNXRuntimeContainer/ONNXRuntimeContainer';
import {OpenVXRuntimeStreamContainer} from './OpenVXRuntimeStreamContainer';
import {toast} from '../../base-components/StudioToast';
import {updateParameters} from '../../api/postprocessor/UpdateParameters';
import './InferenceApp.scss';
import {Parameter} from '../../types/model/framework/Parameter';
import {
  SPA_UPDATE_URL,
  updateSinglePageApplication,
} from '../../api/application/UpdateSpa';
import {
  getInferenceApplication,
  getInferenceApplicationsByApplicationId,
  getNewOrExistingInferenceApplication,
} from './GetInferenceApplication';
import {BatchConfigPanel} from './BatchConfigPanel';
import {StreamConfigPanel} from './StreamConfigPanel';
import {InputSource} from '../../types/deployment/InferenceInputSource';
import {assertResponseBody} from '../../api/request';
import {getPublishedDeviceList} from '../../api/device/DeviceList';
import {StreamingDevice} from '../../types/deployment/StreamingDeviceResponse';
import {useInferenceAppTask} from './useInferenceAppTask';
import {getSubmitFunction, SubmitRequest} from './SubmitInferenceApplication';
import {InferenceAppForm, InferenceAppExecution} from './InferenceAppTypes';
import {NodeSource, ProductionNode} from '../../api/deployment/DeploymentNodes';
import {useEdgeInference} from './useEdgeInference';
import {EdgeStreamContainer} from './EdgeStreamContainer';
import {Deployment} from '../../types/deployment/DeploymentResponse';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../TestDeployView/HlsVideoStream';
import {useApplicationDetails} from '../../types/project/ApplicationDetails';
import {getGspList} from '../../api/gsp/GspList';
import {GspListItem} from '../../types/gsp/GspListItem';
import {
  PROJECT_SETTINGS,
  REQUEST_POLLING_INTERVAL,
  TEST_PANEL_SETTINGS,
} from '../../config/constants';
import {StudioTextField} from '../../base-components/StudioTextField';
import {StudioSelect} from '../../base-components/StudioSelect';
import {
  fetchDeploymentDetails,
  stopDeploymentStateless,
} from '../../store/prodDeployments/dashboard';

export const CreateInferenceAppPage = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const {projectId, applicationId, deploymentId} = useParams<{
    projectId: string;
    applicationId: string;
    deploymentId: string | undefined;
  }>();
  const intl = useIntl();
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location]);
  const defaultSpaId = useMemo(() => search.get('spaId'), [search]);
  const viewOnly = useMemo(() => search.get('viewOnly'), [search]); // this is for making the input not editable in case of editing SPA test
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [userCancelled, setUserCancelled] = useState<boolean>(false);
  const [ininialized, setInitialized] = useState(false);
  const [spaRetake, setSpaRetake] = useState(true);
  const [executionContext, setExecutionContext] = useState<InferenceAppExecution>();
  const [selectedTab, setSelectedTab] = useState<InputType | ''>('');
  const [deploymentNode, setDeploymentNode] = useState<ProductionNode>();
  const [streamingDevices, setStreamingDevices] = useState<StreamingDevice[]>([]);
  const [nodeSources, setNodeSources] = useState<NodeSource[]>([]);
  const [batchConfigFieldsReady, setBatchConfigFieldsReady] = useState(false);
  const [streamConfigFieldsReady, setStreamConfigFieldsReady] = useState(false);
  const [streamSubject, setStreamSubject] = useState<VideoStreamSubject>();
  const [, setGspList] = useState<GspListItem[]>();

  const deploymentDetails = useSelector(
    (state: RootState) => state.deploymentDashboard.details
  );
  const projectData: Project = useSelector((state: RootState) => state.project.data);
  const application = Object.values(projectData.applications).find(
    application => application.id === applicationId
  );
  const hasIsp = Boolean(application?.transformation?.transformationType === 'ISP');

  const [prodDeployment, setProdDeployment] = useState<Deployment>();
  const [prodDeploymentStopping, setProdDeploymentStopping] = useState<Boolean>(false);
  const {handleInferenceStart, handleInferenceStop} = useInferenceAppTask(projectId);
  const {
    handleEdgeInferenceStart,
    handleEdgeInferenceStop,
    fetchDeploymentNode,
  } = useEdgeInference(setProdDeployment);
  const [spaList, setSpaList] = useState<SinglePageApplication[]>([]);

  const [spa, setSpaState] = useState<SinglePageApplication>();
  const setSpa = (newSpa?: SinglePageApplication) => {
    setSpaState(newSpa);
    if (newSpa) {
      var url = new URL(window.location.href);
      url.searchParams.set('spaId', newSpa.id || '');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  };

  const err = (x?: string) => {
    if (x) {
      toast.error(x);
    }
  };

  const applicationDetails = useApplicationDetails(applicationId);
  const isONNXWeb = application?.runtimeType === 'ONNX_RUNTIME_WEB';
  const pre = FetchPreProcessor(application, err);
  const modelPost = FetchModelPostProcessor(application, err);
  const appPost =
    application && spa
      ? FetchSPAPostProcessor(application, spa, err)
      : FetchAppPostProcessor(application, err);
  const onnxWebImagesSupported = supportsImages(pre, modelPost, appPost);
  const onnxWebVideoSupported = supportsVideo(pre, modelPost, appPost);
  const imagesSupported =
    application?.inputSources.includes('IMAGES') &&
    (!isONNXWeb || onnxWebImagesSupported);
  const videoTypes = application?.inputSources.filter((is: InputSource) =>
    SPA_VIDEO_SOURCES.has(is)
  );
  const videoSupported =
    videoTypes &&
    videoTypes.length > 0 &&
    !hasIsp &&
    (!isONNXWeb || onnxWebVideoSupported);

  async function initialSpaFetch(
    inputType: InputType | '',
    projectId: string,
    applicationId: string,
    deploymentId?: string
  ) {
    if (!spaRetake) {
      setSpaRetake(true);
      return;
    }
    if (defaultSpaId && !ininialized) {
      let newTabIndex = undefined;
      const spaResponse = await getInferenceApplication(defaultSpaId, applicationId);
      if (!spaResponse) {
        toast.error(intl.formatMessage({id: 'inferenceApp.error.notfound'}));
      } else {
        setSpa(spaResponse);
        newTabIndex = spaResponse.input.type;
      }
      setInitialized(true);
      if (newTabIndex && newTabIndex !== selectedTab) {
        setSpaRetake(false);
        setSelectedTab(newTabIndex);
      }
    } else if (inputType) {
      const targetType = deploymentId ? 'PRODUCTION' : 'TEST';
      const spaResponse = await getNewOrExistingInferenceApplication(
        projectId,
        applicationId,
        deploymentId,
        inputType,
        targetType
      );
      if (!spaResponse) {
        toast.error(intl.formatMessage({id: 'inferenceApp.error.notfound'}));
      } else {
        setSpa(spaResponse);
      }
    }
  }

  async function fetchGspList(projectId: string, applicationId: string) {
    const gspList = await assertResponseBody(getGspList(projectId, applicationId));
    if (gspList && (gspList.length > 1 || !gspList[0].id.toLowerCase().includes('gsp'))) {
      setGspList(gspList);
    }
  }

  useEffect(() => {
    const fetchSPAList = async () => {
      const list = await getInferenceApplicationsByApplicationId(applicationId);
      if (list) {
        setSpaList(list);
      }
    };
    if (applicationId) {
      fetchSPAList();
    }
  }, [applicationId]);

  useEffect(() => {
    initialSpaFetch(selectedTab, projectId, applicationId, deploymentId);
    fetchGspList(projectId, applicationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, applicationId, deploymentId, selectedTab]);

  useEffect(() => {
    const fetchStreamingDevicesList = async () => {
      const devices = await assertResponseBody(getPublishedDeviceList());
      if (devices) {
        setStreamingDevices(devices);
      }
    };

    fetchStreamingDevicesList();
    setStreamSubject(new VideoStreamSubject());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDeploymentNode(setDeploymentNode, setNodeSources, deploymentId);
    let interval: NodeJS.Timeout;

    if (deploymentId) {
      interval = setInterval(() => {
        dispatch(fetchDeploymentDetails(deploymentId));
      }, REQUEST_POLLING_INTERVAL);
    }

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId]);

  const {data: ppListData} = usePostProcessorList({projectId});
  const projectPostProcessors = ppListData
    ? ppListData.existingPostProcessors.filter(
        pp =>
          pp.executionContainer === 'BROWSER' && pp.postProcessorType === 'APPLICATION'
      )
    : [];

  useEffect(() => {
    dispatch(projectFetch(projectId));
  }, [projectId, dispatch, spa]);

  const updateSpa = async (
    publishToOrg: boolean,
    exposeAPI?: boolean,
    exposeInfPage?: boolean,
    postProcessorId?: string | null,
    schedulerId?: string | null,
    name?: string
  ) => {
    if (!spa) {
      return;
    }
    var updateRequest: SinglePageApplication = {
      id: spa.id,
      name: name || spa?.name,
      projectId,
      applicationId,
      input: {
        type: spa.input.type,
        exposeAPI: exposeAPI ? exposeAPI : spa.input.exposeAPI,
        exposeInferencePage: exposeInfPage
          ? exposeInfPage
          : spa.input.exposeInferencePage,
      },
      target: {
        type: spa.target.type,
        deploymentId,
        schedulerId: schedulerId ? schedulerId : undefined,
      },
      postProcessorId: postProcessorId ? postProcessorId : spa.postProcessorId,
      published: spa.published || publishToOrg,
    };
    return await updateSinglePageApplication([SPA_UPDATE_URL, updateRequest]);
  };

  const updateApplication = async (values: Partial<InferenceAppForm>) => {
    var updatedApplication = application;
    const {data} = await updateParameters({
      projectId,
      applicationId,
      spaId: spa?.id,
      parameters: [
        ...(values.postProcessorParams || []),
        ...(values.preProcessorParams || []),
      ],
    });
    updatedApplication = data.body;
    return updatedApplication;
  };

  const update = async (publishToOrg: boolean, values: Partial<InferenceAppForm>) => {
    const updatedApplication = await updateApplication(values);
    const updatedSpa = await updateSpa(
      publishToOrg,
      values.exposeAPI,
      values.exposeInferencePage,
      values.postProcessorId,
      values.schedulerId,
      values.name
    );
    return {updatedSpa, updatedApplication};
  };

  const handlePublish = async (values: InferenceAppForm) => {
    try {
      if (values.name.trim() === '') {
        toast.error(intl.formatMessage({id: 'inferenceApp.error.name'}));
        return;
      }
      const {updatedSpa} = await update(true, values);
      if (!updatedSpa) {
        return;
      }
      setSpa(updatedSpa);
      handleRedirect();
    } catch (e) {
      console.error(e);
    }
  };

  const handleRedirect = async () => {
    history.push(
      `/spa/project/${projectId}/application/${applicationId}/inference/${spa?.id ||
        ''}/details`
    );
  };

  const handleCancel = async (optId?: string, subject?: VideoStreamSubject) => {
    setUserCancelled(true);
    if (spa?.input.type === 'STREAM' && spa?.target.type === 'PRODUCTION') {
      if (prodDeploymentStopping) {
        return;
      }
      if (
        deploymentDetails?.status === 'STARTING' ||
        deploymentDetails?.status === 'RUNNING'
      ) {
        handleEdgeInferenceStop(() => {
          if (deploymentId && !prodDeploymentStopping) {
            setProdDeploymentStopping(true);
            try {
              stopDeploymentStateless(deploymentId);
            } catch (e) {
              console.error(e);
            }
          }
          setExecutionContext(undefined);
          setProdDeploymentStopping(false);
        });
      } else {
        handleInferenceStop(() => {
          setExecutionContext(undefined);
        }, optId);
      }
    } else {
      setExecutionContext(undefined);
      setProdDeploymentStopping(false);
    }
    setInProgress(false);
    if (subject) {
      subject.removeListener('CreateSpaStreamObserver');
    }
  };

  const handleSubmit = async (values: InferenceAppForm) => {
    try {
      if (inProgress) {
        return;
      }
      setInProgress(true);
      setUserCancelled(false);
      let updatedSpa = spa;
      let updatedApplication = application;
      if (application?.runtimeType !== 'ONNX_RUNTIME_WEB') {
        const {updatedSpa, updatedApplication} = await update(false, values);
        if (!updatedSpa || !updatedApplication) {
          return;
        }
        setSpa(updatedSpa);
      }

      if (!updatedSpa || !updatedApplication || !streamSubject || !applicationDetails) {
        return;
      }

      streamSubject.addListener('CreateSpaStreamObserver', new CreateSpaStreamObserver());

      const configuredParameters = (
        values.postProcessorParams?.map<Parameter>(param => ({
          id: param.id,
          name: param.name,
          value: param.value,
        })) || []
      ).concat(
        values.preProcessorParams?.map<Parameter>(param => ({
          id: param.id,
          name: param.name,
          value: param.value,
        })) || []
      );

      const submitRequest: SubmitRequest = {
        spa: updatedSpa,
        application: updatedApplication,
        applicationDetails,
        configuredParameters,
        streamingDevices,
        streamSubject,
        values,
        handleInferenceStart,
        handleEdgeInferenceStart,
        setExecutionContext,
        onError: e => {
          handleStartError(e, updatedSpa);
        },
      };

      getSubmitFunction(submitRequest)(submitRequest);
    } catch (e) {
      handleStartError(e as Error);
    }
  };

  const handleStartError = (e: Error, spa?: SinglePageApplication) => {
    toast.error(e);
    if (inProgress && spa?.target.type === 'PRODUCTION') {
      handleCancel();
    } else {
      setInProgress(false);
      console.error(e);
    }
  };

  const handlePostProcessorChange = async (
    event: React.ChangeEvent<{
      value: unknown;
    }>,
    spaName: string | undefined
  ) => {
    if (event.target.value === '') {
      event.target.value = null;
    }
    const updatedSpa = await updateSpa(
      false,
      spa?.input.exposeAPI,
      spa?.input.exposeInferencePage,
      event.target.value ? `${event.target.value}` : spa?.postProcessorId,
      spa?.target.schedulerId,
      spaName
    );
    setSpa(updatedSpa);
  };

  const handleNameChange = async (spaName: string) => {
    const updatedSpa = await updateSpa(
      false,
      spa?.input.exposeAPI,
      spa?.input.exposeInferencePage,
      spa?.postProcessorId,
      spa?.target.schedulerId,
      spaName
    );
    setSpa(updatedSpa);
  };

  const onCheckboxChanged = async (
    field: FieldInputProps<boolean>,
    event: React.ChangeEvent<HTMLInputElement>,
    spaName: string | undefined
  ) => {
    const isAPI = field.name.includes('API');
    const updatedSpa = await updateSpa(
      false,
      isAPI ? event.target.checked : spa?.input.exposeAPI,
      isAPI ? spa?.input.exposeInferencePage : event.target.checked,
      spa?.postProcessorId,
      spa?.target.schedulerId,
      spaName
    );
    setSpa(updatedSpa);
  };

  class CreateSpaStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.STREAM_FINISHED:
          handleCancel(event.deploymentId, event.subject);
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            toast.error(event.message);
          }
          handleCancel(event.deploymentId, event.subject);
          break;
        default:
          break;
      }
    }
  }

  const checkForSPA = useCallback(
    (type: InputType) => {
      const targetType = deploymentId ? 'PRODUCTION' : 'TEST';
      const foundSpa = spaList.find(
        spa => spa.input.type === type && spa.target.type === targetType
      );
      return foundSpa;
    },
    [spaList, deploymentId]
  );

  const inputTypeOptions = useMemo(() => {
    const batchExists = checkForSPA('BATCH');
    const streamExists = checkForSPA('STREAM');
    const batchValue = `${intl.formatMessage({
      id: 'inferenceApp.batch',
    })} ${
      batchExists
        ? intl.formatMessage({id: 'inferenceApp.exists'})
        : intl.formatMessage({id: 'inferenceApp.create'})
    }`;
    const streamValue = `${intl.formatMessage({
      id: 'inferenceApp.stream',
    })} ${
      streamExists
        ? intl.formatMessage({id: 'inferenceApp.exists'})
        : intl.formatMessage({id: 'inferenceApp.create'})
    }`;
    return {
      BATCH: batchValue,
      STREAM: streamValue,
    };
  }, [intl, checkForSPA]);

  const [runningInference, setIsRunningInference] = useState(false);

  return (
    <Formik<InferenceAppForm>
      initialValues={{
        name:
          spa?.name ||
          application?.name?.slice(0, TEST_PANEL_SETTINGS.name.maxLength) ||
          '',
        exposeAPI: spa?.input.exposeAPI || false,
        exposeInferencePage: spa?.input.exposeInferencePage || false,
        postProcessorId: spa?.postProcessorId,
        images: [],
        runtimeMode: 'webgl',
        inferenceSource: 'IP_CAMERA',
        ipCamera: '',
        deviceCamera: '',
        schedulerId: undefined,
        postProcessorParams: [],
        preProcessorParams: [],
      }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({values, handleChange, handleBlur, touched}) => (
        <div className="inference-app">
          <ReturnToPreviousPageButton className="inference-app__return" />
          <div className="inference-app__body">
            <Form className="inference-app__panel">
              <div className="inference-app__title">
                <FormattedMessage id="inferenceApp.title" tagName="h2" />
              </div>
              <div className="inference-app__panel__container">
                {application && (
                  <div className="inference-app__panel__container__form">
                    <StudioTextField
                      inputProps={{maxLength: PROJECT_SETTINGS.name.maxLength}}
                      name="base_name"
                      margin="normal"
                      id="base_name"
                      label={intl.formatMessage({
                        id: 'inferenceApp.app',
                      })}
                      required
                      fullWidth
                      className="name-field"
                      disabled={true}
                      data-testid="create-inference-app-base-name"
                      useFieldProps={false}
                      value={application.name}
                    />
                    <StudioTextField
                      inputProps={{maxLength: PROJECT_SETTINGS.name.maxLength}}
                      name="name"
                      margin="normal"
                      id="name"
                      label={intl.formatMessage({
                        id: 'inferenceApp.singlePageAppName',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'inferenceApp.singlePageAppName',
                      })}
                      required
                      fullWidth
                      className="name-field"
                      disabled={inProgress}
                      data-testid="create-inference-app-name"
                      useFieldProps={false}
                      value={values.name}
                      strategy="formik"
                      onChange={handleChange}
                      onBlur={e => {
                        handleNameChange(e.target.value);
                        handleBlur(e);
                      }}
                      error={touched.name && values.name.length === 0}
                    />
                    <StudioSelect
                      className={cn(
                        'create-prod-deploy__field',
                        selectedTab
                          ? 'create-prod-deploy__select__selected'
                          : 'create-prod-deploy__select__unselected'
                      )}
                      dataTestId="create-prod-deploy-application"
                      label={intl.formatMessage({id: 'inferenceApp.selectInputType'})}
                      options={inputTypeOptions}
                      placeholder={intl.formatMessage({
                        id: 'createDeploy.selectValue',
                      })}
                      disablePlaceholder
                      SelectProps={{
                        value: selectedTab,
                        onChange: (e: React.ChangeEvent<any>) => {
                          const found = checkForSPA(e.target.value);
                          if (found) {
                            let url = new URL(window.location.href);
                            url.searchParams.set('spaId', found.id || '');
                            url.searchParams.set('viewOnly', 'true');
                            window.location.replace(url.toString());
                          }
                          setSelectedTab(e.target.value);
                        },
                      }}
                      disabled={inProgress || !!viewOnly}
                      required
                    />
                  </div>
                )}
                {application && spa && (
                  <>
                    {spa?.input.type === 'BATCH' ? (
                      <>
                        {imagesSupported && (
                          <BatchConfigPanel
                            application={application}
                            inProgress={inProgress}
                            values={values}
                            spa={spa}
                            projectPostProcessors={projectPostProcessors}
                            handlePostProcessorChange={(...args) =>
                              handlePostProcessorChange(...args, values.name)
                            }
                            onCheckboxChanged={(...args) =>
                              onCheckboxChanged(...args, values.name)
                            }
                            setReady={setBatchConfigFieldsReady}
                          />
                        )}
                        {!imagesSupported && (
                          <FormattedMessage id="inferenceApp.batch.not.supported" />
                        )}
                      </>
                    ) : spa?.input.type === 'STREAM' ? (
                      <>
                        {videoSupported && (
                          <StreamConfigPanel
                            application={application}
                            inProgress={inProgress}
                            values={values}
                            spa={spa}
                            projectPostProcessors={projectPostProcessors}
                            streamingDevices={streamingDevices}
                            nodeSources={nodeSources}
                            handlePostProcessorChange={(...args) =>
                              handlePostProcessorChange(...args, values.name)
                            }
                            onCheckboxChanged={(...args) =>
                              onCheckboxChanged(...args, values.name)
                            }
                            setReady={setStreamConfigFieldsReady}
                          />
                        )}
                        {!videoSupported && (
                          <FormattedMessage id="inferenceApp.stream.not.supported" />
                        )}
                      </>
                    ) : null}
                  </>
                )}
              </div>
              <div className="inference-app__submit">
                {!userCancelled &&
                  selectedTab === 'STREAM' &&
                  (deploymentDetails?.status === 'RUNNING' ||
                    deploymentDetails?.status === 'STARTING') &&
                  deploymentDetails?.source?.format !== 'IMAGES' && (
                    <p>
                      {deploymentDetails?.permanent ? (
                        <FormattedMessage id="inferenceApp.inferenceIsRunning" />
                      ) : !executionContext && !deploymentDetails?.permanent ? (
                        <FormattedMessage id="inferenceApp.deviceInUse" />
                      ) : null}
                    </p>
                  )}
                {deploymentDetails?.status === 'STOPPING' && (
                  <p>
                    <FormattedMessage id="prodDeployment.stopping" />
                  </p>
                )}
                <div className="inference-app__submit__buttons">
                  {inProgress ? (
                    <Button
                      color="secondary"
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        if (spa?.input.type === 'STREAM') {
                          streamSubject?.raiseStreamFinishEvent();
                        } else {
                          handleCancel();
                        }
                      }}
                    >
                      <FormattedMessage id="action.cancel" />
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      disabled={
                        inProgress ||
                        (deploymentId && !deploymentNode) ||
                        (selectedTab === 'BATCH' && !batchConfigFieldsReady) ||
                        (selectedTab === 'STREAM' && !streamConfigFieldsReady) ||
                        (selectedTab === 'STREAM' &&
                          deploymentDetails?.status === 'RUNNING' &&
                          deploymentDetails?.source?.format !== 'IMAGES') ||
                        (deploymentDetails != null && deploymentDetails.status !== 'IDLE')
                      }
                      onClick={() => handleSubmit(values)}
                    >
                      <FormattedMessage id="inferenceApp.test" />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={
                      inProgress ||
                      values.name.length === 0 ||
                      (!values.exposeAPI && !values.exposeInferencePage) ||
                      (values.exposeInferencePage &&
                        spa?.input.type === 'BATCH' &&
                        !values.postProcessorId)
                    }
                    onClick={() => handlePublish(values)}
                  >
                    <FormattedMessage id="inferenceApp.publish" />
                  </Button>
                </div>
              </div>
            </Form>
            <section className="inference-app__results">
              {spa?.published && (
                <div className="inference-app__menu">
                  <Button
                    className="inference-app__back"
                    variant="contained"
                    disableElevation
                    disabled={inProgress}
                    onClick={() => handleRedirect()}
                  >
                    <FormattedMessage id="deployments.viewDetails" />
                  </Button>
                </div>
              )}
              <h5>
                {intl.formatMessage({
                  id:
                    inProgress && !runningInference
                      ? 'form.loading'
                      : 'prodDeployment.inferenceResults',
                })}
              </h5>
              {inProgress && <LoadingSkeleton count={values.images.length} />}
              <div
                id="inference-results-element"
                className={cn(inProgress && 'inference-app--hidden')}
              ></div>
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'BATCH' && (
                  <OpenVXRuntimeContainer
                    projectId={projectId}
                    application={executionContext.application}
                    spa={executionContext.spa}
                    images={executionContext.images}
                    inProgress={inProgress}
                    onError={() => {
                      setInProgress(false);
                      setIsRunningInference(false);
                    }}
                    onFinish={() => {
                      setInProgress(false);
                      setIsRunningInference(false);
                    }}
                    onRunning={() => setIsRunningInference(true)}
                  />
                )}
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'STREAM' &&
                executionContext.spa.target.type === 'TEST' &&
                executionContext.streamDeploymentId &&
                streamSubject && (
                  <OpenVXRuntimeStreamContainer
                    projectId={projectId}
                    deploymentId={executionContext.streamDeploymentId}
                    spaId={executionContext.spa.id}
                    spaPostProcessorId={executionContext.spa.postProcessorId}
                    displayRealtimeParameters={true}
                    streamSubject={streamSubject}
                    onError={() => {
                      setIsRunningInference(false);
                    }}
                    onFinish={() => {
                      setIsRunningInference(false);
                    }}
                    onRunning={() => setIsRunningInference(true)}
                  />
                )}
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'STREAM' &&
                executionContext.spa.target.type === 'PRODUCTION' &&
                executionContext.spa.target.deploymentId &&
                streamSubject && (
                  <>
                    {deploymentNode && prodDeployment && (
                      <EdgeStreamContainer
                        projectId={projectId}
                        deploymentId={prodDeployment.id}
                        streamSubject={streamSubject}
                        onError={() => {
                          setIsRunningInference(false);
                        }}
                        onFinish={() => {
                          setIsRunningInference(false);
                        }}
                        onRunning={() => setIsRunningInference(true)}
                      />
                    )}
                    {!deploymentNode && (
                      <FormattedMessage id="deployments.noTargetDevice" />
                    )}
                  </>
                )}
              {executionContext?.type === 'ONNX_RUNTIME_WEB' && (
                <ONNXRuntimeContainer
                  key={executionContext.id}
                  type={executionContext.inferenceSource}
                  projectId={projectId}
                  application={executionContext.application}
                  runtimeMode={executionContext.runtimeMode}
                  spa={executionContext.spa}
                  configuredParams={executionContext.configuredParams}
                  displayRealtimeParameters={true}
                  url={executionContext.onnxVideoURL}
                  files={executionContext.images}
                  inProgress={inProgress}
                  onRunning={() => setIsRunningInference(true)}
                  onError={message => {
                    if (message) {
                      toast.error(message);
                    }
                    setInProgress(false);
                    setIsRunningInference(false);
                    dispatch(setONNXTask({status: TASK_STATUS.IDLE}));
                  }}
                  onFinish={() => {
                    setInProgress(false);
                    setIsRunningInference(false);
                    dispatch(setONNXTask({status: TASK_STATUS.COMPLETE}));
                  }}
                />
              )}
            </section>
          </div>
        </div>
      )}
    </Formik>
  );
};
